import { useCallback } from 'react';
import { useAuth } from '../auth/AuthProvider';

export interface FirstSeenResult {
    /** True iff this card hasn't been seen, prefs have loaded, and the
     *  user hasn't suppressed first-time tips. */
    shouldShow: boolean;
    /** Mark the card as seen. Idempotent and optimistic — UI hides
     *  immediately, server confirms in the background. */
    markSeen: () => void;
}

/** Hook for contextual first-time-only cards. Each card is keyed by a
 *  short string (e.g. ``"case_view"``). The hook reads the user's
 *  prefs from AuthProvider and gates the card's render with
 *  ``shouldShow``; the card calls ``markSeen`` from its dismiss button.
 *
 *  Cards never re-show for a given user once dismissed. The "Re-enable
 *  tips" affordance (TBD in the help panel) clears state via a
 *  separate PATCH.
 */
export const useFirstSeen = (key: string): FirstSeenResult => {
    const { isLoading, isAuthenticated, prefs, markSeen } = useAuth();

    const shouldShow =
        !isLoading
        && isAuthenticated
        && !prefs.suppress_all
        && !prefs.seen.includes(key);

    const mark = useCallback(() => {
        void markSeen(key);
    }, [markSeen, key]);

    return { shouldShow, markSeen: mark };
};
